import { useState, useEffect, useCallback } from 'react';
import {
  ToggleButtonVariant,
  type ToggleButtonState,
  type ToggleButtonSize,
} from './types';

// ============================================================================
// Variant Color Configurations
// ============================================================================

// Variant color configurations for checked state
export const checkedVariantColors = {
  [ToggleButtonVariant.Primary]: {
    enabled: 'bg-primary-color-40',
    hovered: 'bg-primary-color-50',
    focused: 'bg-primary-color-40',
    pressed: 'bg-primary-color-30',
    text: 'text-neutral-100',
    focusRing: 'ring-primary-color-60',
  },
  [ToggleButtonVariant.Secondary]: {
    enabled: 'bg-secondary-color-40',
    hovered: 'bg-secondary-color-50',
    focused: 'bg-secondary-color-40',
    pressed: 'bg-secondary-color-30',
    text: 'text-neutral-0',
    focusRing: 'ring-secondary-color-60',
  },
  [ToggleButtonVariant.Success]: {
    enabled: 'bg-success-40',
    hovered: 'bg-success-50',
    focused: 'bg-success-40',
    pressed: 'bg-success-30',
    text: 'text-neutral-100',
    focusRing: 'ring-success-60',
  },
  [ToggleButtonVariant.Error]: {
    enabled: 'bg-error-40',
    hovered: 'bg-error-50',
    focused: 'bg-error-40',
    pressed: 'bg-error-30',
    text: 'text-neutral-100',
    focusRing: 'ring-error-60',
  },
  [ToggleButtonVariant.Warning]: {
    enabled: 'bg-warning-40',
    hovered: 'bg-warning-50',
    focused: 'bg-warning-40',
    pressed: 'bg-warning-30',
    text: 'text-neutral-0',
    focusRing: 'ring-warning-60',
  },
  [ToggleButtonVariant.Information]: {
    enabled: 'bg-information-40',
    hovered: 'bg-information-50',
    focused: 'bg-information-40',
    pressed: 'bg-information-30',
    text: 'text-neutral-100',
    focusRing: 'ring-information-60',
  },
};

// Variant color configurations for unchecked state
export const uncheckedVariantColors = {
  [ToggleButtonVariant.Primary]: {
    enabled: 'bg-neutral-20',
    hovered: 'bg-neutral-30',
    focused: 'bg-neutral-20',
    pressed: 'bg-neutral-25',
    text: 'text-neutral-100',
    focusRing: 'ring-primary-color-60',
  },
  [ToggleButtonVariant.Secondary]: {
    enabled: 'bg-neutral-20',
    hovered: 'bg-neutral-30',
    focused: 'bg-neutral-20',
    pressed: 'bg-neutral-25',
    text: 'text-neutral-100',
    focusRing: 'ring-secondary-color-60',
  },
  [ToggleButtonVariant.Success]: {
    enabled: 'bg-neutral-20',
    hovered: 'bg-neutral-30',
    focused: 'bg-neutral-20',
    pressed: 'bg-neutral-25',
    text: 'text-neutral-100',
    focusRing: 'ring-success-60',
  },
  [ToggleButtonVariant.Error]: {
    enabled: 'bg-neutral-20',
    hovered: 'bg-neutral-30',
    focused: 'bg-neutral-20',
    pressed: 'bg-neutral-25',
    text: 'text-neutral-100',
    focusRing: 'ring-error-60',
  },
  [ToggleButtonVariant.Warning]: {
    enabled: 'bg-neutral-20',
    hovered: 'bg-neutral-30',
    focused: 'bg-neutral-20',
    pressed: 'bg-neutral-25',
    text: 'text-neutral-100',
    focusRing: 'ring-warning-60',
  },
  [ToggleButtonVariant.Information]: {
    enabled: 'bg-neutral-20',
    hovered: 'bg-neutral-30',
    focused: 'bg-neutral-20',
    pressed: 'bg-neutral-25',
    text: 'text-neutral-100',
    focusRing: 'ring-information-60',
  },
};

// Size configurations for toggle switch
export const sizeConfig = {
  XSmall: {
    trackWidth: 32,
    trackWidthWithText: 52,
    trackHeight: 18,
    thumbSize: 14,
    thumbOffset: 2,
    fontSize: 12,
    lineHeight: 16,
    textFontSize: 11,
    textLineHeight: 16,
    textLetterSpacing: 0.5,
    gap: 8,
    textPadding: 6,
  },
  Small: {
    trackWidth: 40,
    trackWidthWithText: 60,
    trackHeight: 22,
    thumbSize: 18,
    thumbOffset: 2,
    fontSize: 14,
    lineHeight: 20,
    textFontSize: 12,
    textLineHeight: 16,
    textLetterSpacing: 0.5,
    gap: 10,
    textPadding: 8,
  },
  Medium: {
    trackWidth: 48,
    trackWidthWithText: 72,
    trackHeight: 26,
    thumbSize: 22,
    thumbOffset: 2,
    fontSize: 16,
    lineHeight: 24,
    textFontSize: 12,
    textLineHeight: 16,
    textLetterSpacing: 0.5,
    gap: 12,
    textPadding: 10,
  },
  Large: {
    trackWidth: 56,
    trackWidthWithText: 84,
    trackHeight: 32,
    thumbSize: 28,
    thumbOffset: 2,
    fontSize: 22,
    lineHeight: 28,
    textFontSize: 14,
    textLineHeight: 20,
    textLetterSpacing: 0.1,
    gap: 14,
    textPadding: 12,
  },
  XLarge: {
    trackWidth: 64,
    trackWidthWithText: 96,
    trackHeight: 36,
    thumbSize: 32,
    thumbOffset: 2,
    fontSize: 32,
    lineHeight: 40,
    textFontSize: 14,
    textLineHeight: 20,
    textLetterSpacing: 0.1,
    gap: 16,
    textPadding: 14,
  },
};

// ============================================================================
// Hook Props & Return Types
// ============================================================================

export interface UseToggleButtonProps {
  checked?: boolean | undefined;
  onChange?: ((checked: boolean) => void) | undefined;
  onText?: string | undefined;
  offText?: string | undefined;
  variant?: ToggleButtonVariant | undefined;
  size?: ToggleButtonSize | undefined;
  state?: ToggleButtonState | undefined;
  disabled?: boolean | undefined;
  onClick?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
  onFocus?: ((e: React.FocusEvent<HTMLButtonElement>) => void) | undefined;
  onBlur?: ((e: React.FocusEvent<HTMLButtonElement>) => void) | undefined;
  onMouseEnter?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
  onMouseLeave?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
  onMouseDown?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
  onMouseUp?: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
}

export interface ToggleButtonStateStyles {
  backgroundColor: string;
  color: string;
  cursor: string;
}

export interface UseToggleButtonReturn {
  config: (typeof sizeConfig)[ToggleButtonSize];
  isDisabled: boolean;
  internalChecked: boolean;
  stateStyles: ToggleButtonStateStyles;
  trackWidth: number;
  thumbPosition: number;
  handleClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleMouseDown: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleMouseUp: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleFocus: (e: React.FocusEvent<HTMLButtonElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLButtonElement>) => void;
}

// ============================================================================
// useToggleButton Hook
// ============================================================================

export const useToggleButton = ({
  checked = false,
  onChange,
  onText,
  offText,
  variant = ToggleButtonVariant.Primary,
  size = 'Small',
  state = 'Enabled',
  disabled,
  onClick,
  onFocus,
  onBlur,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
}: UseToggleButtonProps): UseToggleButtonReturn => {
  const config = sizeConfig[size];
  const isDisabled = disabled || state === 'Disabled';
  const [internalChecked, setInternalChecked] = useState(checked);

  // Sync internal state with controlled prop
  useEffect(() => {
    setInternalChecked(checked);
  }, [checked]);

  // Handle state transitions
  const [internalState, setInternalState] = useState<ToggleButtonState>(state);

  useEffect(() => {
    if (state !== 'Enabled') {
      setInternalState(state);
    }
  }, [state]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isDisabled) {
        const newChecked = !internalChecked;
        setInternalChecked(newChecked);
        onChange?.(newChecked);
      }
      onClick?.(e);
    },
    [isDisabled, internalChecked, onChange, onClick]
  );

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isDisabled && state === 'Enabled') {
        setInternalState('Hovered');
      }
      onMouseEnter?.(e);
    },
    [isDisabled, state, onMouseEnter]
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isDisabled && state === 'Enabled') {
        setInternalState('Enabled');
      }
      onMouseLeave?.(e);
    },
    [isDisabled, state, onMouseLeave]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isDisabled && state === 'Enabled') {
        setInternalState('Pressed');
      }
      onMouseDown?.(e);
    },
    [isDisabled, state, onMouseDown]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!isDisabled && state === 'Enabled') {
        setInternalState('Hovered');
      }
      onMouseUp?.(e);
    },
    [isDisabled, state, onMouseUp]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      if (!isDisabled && state === 'Enabled') {
        setInternalState('Focused');
      }
      onFocus?.(e);
    },
    [isDisabled, state, onFocus]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLButtonElement>) => {
      if (!isDisabled && state === 'Enabled') {
        setInternalState('Enabled');
      }
      onBlur?.(e);
    },
    [isDisabled, state, onBlur]
  );

  // State-based styling
  const getStateStyles = useCallback((): ToggleButtonStateStyles => {
    const currentStateToUse = state !== 'Enabled' ? state : internalState;
    const variantColors = internalChecked
      ? checkedVariantColors[variant]
      : uncheckedVariantColors[variant];

    if (isDisabled) {
      return {
        backgroundColor: 'bg-neutral-30',
        color: 'text-neutral-60',
        cursor: 'not-allowed',
      };
    }

    switch (currentStateToUse) {
      case 'Hovered':
        return {
          backgroundColor: variantColors.hovered,
          color: variantColors.text,
          cursor: 'pointer',
        };
      case 'Focused':
        return {
          backgroundColor: variantColors.focused,
          color: variantColors.text,
          cursor: 'pointer',
        };
      case 'Pressed':
        return {
          backgroundColor: variantColors.pressed,
          color: variantColors.text,
          cursor: 'pointer',
        };
      default:
        return {
          backgroundColor: variantColors.enabled,
          color: variantColors.text,
          cursor: 'pointer',
        };
    }
  }, [state, internalState, internalChecked, variant, isDisabled]);

  const stateStyles = getStateStyles();

  // Calculate dynamic track width based on text content
  const calculateTrackWidth = useCallback((): number => {
    if (!onText && !offText) {
      return config.trackWidth;
    }

    const baseWidth = config.thumbSize + config.thumbOffset * 2;
    const textSpace = config.textPadding * 2;
    const charWidth = config.textFontSize * 0.7;
    const maxTextLength = Math.max(onText?.length || 0, offText?.length || 0);
    const estimatedTextWidth = maxTextLength * charWidth;
    const requiredWidth = baseWidth + textSpace + estimatedTextWidth;

    return Math.max(config.trackWidthWithText, requiredWidth);
  }, [onText, offText, config]);

  const trackWidth = calculateTrackWidth();

  // Calculate thumb position
  const thumbPosition = internalChecked
    ? trackWidth - config.thumbSize - config.thumbOffset
    : config.thumbOffset;

  return {
    config,
    isDisabled,
    internalChecked,
    stateStyles,
    trackWidth,
    thumbPosition,
    handleClick,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
    handleFocus,
    handleBlur,
  };
};
